import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Cpu, LayoutDashboard, ShoppingCart, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';

interface NavbarProps {
  onAuthOpen: (mode: 'login' | 'register') => void;
  onCartOpen: () => void;
}

export default function Navbar({ onAuthOpen, onCartOpen }: NavbarProps) {
  const { user, profile, signOut } = useAuth();
  const { totalCount } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/50"
      style={{
        background: 'oklch(0.10 0.022 280 / 0.92)',
        backdropFilter: 'blur(16px)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.div
            className="flex items-center gap-2 cursor-pointer select-none"
            whileHover={{ scale: 1.03 }}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}
            >
              <Cpu className="w-5 h-5 text-white" />
            </div>
            <span
              className="text-xl font-bold tracking-widest text-foreground"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              SOKAO<span className="text-primary">-TOYS</span>
            </span>
          </motion.div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold tracking-wide">
            {['KOLEKCJA', 'SCI-FI', 'FANTASY', 'PERSONALIZOWANE'].map((item) => (
              <a
                key={item}
                href="#"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.08em' }}
              >
                {item}
              </a>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Cart button */}
            <button onClick={onCartOpen}
              className="relative flex items-center justify-center w-9 h-9 rounded-lg border transition-colors hover:border-primary/60"
              style={{ background: 'oklch(0.14 0.028 280)', borderColor: 'oklch(0.25 0.045 280)' }}>
              <ShoppingCart className="w-4 h-4 text-foreground" />
              {totalCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white flex items-center justify-center font-black leading-none"
                  style={{ background: 'var(--primary)', fontSize: '0.6rem', fontFamily: 'JetBrains Mono, monospace' }}>
                  {totalCount > 9 ? '9+' : totalCount}
                </span>
              )}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-primary/50 text-primary hover:bg-primary/10 gap-2">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                      {profile?.username ?? 'USER'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-border/60">
                  {profile?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => { window.location.hash = '/admin'; }} className="gap-2 cursor-pointer text-primary">
                      <LayoutDashboard className="w-4 h-4" />
                      Panel Admina
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/orders')} className="gap-2 cursor-pointer">
                    <Package className="w-4 h-4" />
                    Moje zamówienia
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="gap-2 cursor-pointer">
                    <User className="w-4 h-4" />
                    Mój profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => signOut()} className="text-destructive gap-2 cursor-pointer">
                    <LogOut className="w-4 h-4" />
                    Wyloguj
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => onAuthOpen('login')} className="text-muted-foreground hover:text-foreground">
                  Zaloguj
                </Button>
                <Button size="sm" onClick={() => onAuthOpen('register')} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Zarejestruj
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t border-border/30 py-4 flex flex-col gap-4"
          >
            {['KOLEKCJA', 'SCI-FI', 'FANTASY', 'PERSONALIZOWANE'].map((item) => (
              <a
                key={item}
                href="#"
                className="text-muted-foreground hover:text-primary transition-colors px-2 py-1 text-sm font-semibold"
                style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.08em' }}
              >
                {item}
              </a>
            ))}
          </motion.div>
        )}
      </div>
    </motion.header>
  );
}
